import React from 'react';
import PropTypes from 'prop-types';
import { Box, Column, Span, Typography } from '@vp/swan';
import get from 'lodash/get';
import { useTranslations, ReplaceTokens } from '@vp/om-translations';
import Date from '../../shared/Date';
import DateRange from '../../shared/DateRange';
import { AddressDetail } from './AddressDetail';
import { PickupPointDisplay } from './PickupPointDisplay';
import { AddressType } from '../../../constants/addressType';
import { OrderDetailsCardHeading } from './OrderDetailsCardHeading';
import { shouldShowDateRange } from '../../../util/shipmentDetailsUtils';
import useSiteData from '../../../hooks/useSiteData';

export const ShipmentDetails = ({
  shipping = {},
  shipmentMethod = {},
  arrivalDateRange = {},
  columnStyle = {},
}) => {
  const [shippingBillingCard, paymentMessages] = useTranslations([
    'shippingBillingCard',
    'paymentMessages',
  ]);
  const { span, px, py } = columnStyle;
  const addressType = get(shipping, 'type');

  const { locale } = useSiteData();

  const isPickupPoint = addressType === 'pickupPoint';

  const { earliestArrivalDate, latestArrivalDate } = arrivalDateRange;

  const showDate = !!earliestArrivalDate;

  const localizedShipmentNames = get(shipmentMethod, 'localizedShippingData');

  const localizedShipmentMethodName =
    localizedShipmentNames && localizedShipmentNames[locale]
      ? localizedShipmentNames[locale].shippingMethodName
      : null;

  const defaultShipmentMethodName = get(shipmentMethod, 'name');

  const showDateRange = shouldShowDateRange(
    earliestArrivalDate,
    latestArrivalDate
  );

  const showDeliveryDate = () => {
    // If items arrive on different days, show the date range
    if (showDate && showDateRange) {
      return (
        <Typography>
          <ReplaceTokens
            tokenizedString={shippingBillingCard.estimatedArrival}
            substitutions={{
              date: (
                <DateRange
                  start={earliestArrivalDate}
                  end={latestArrivalDate}
                />
              ),
            }}
          />
        </Typography>
      );
    }
    // If all items arrive on the same day, show that date
    if (showDate && !showDateRange) {
      return (
        <Typography>
          <ReplaceTokens
            tokenizedString={shippingBillingCard.estimatedArrival}
            substitutions={{
              date: <Date date={earliestArrivalDate} />,
            }}
          />
        </Typography>
      );
    }
    // If the arrival date is unknown or nonexistent (digital orders), show disclaimer
    return (
      <Typography fontSize="small" textColor="subtle">
        {paymentMessages.deliveryDisclaimer}
      </Typography>
    );
  };
  return (
    <>
      <Column span={span} px={px} py={py}>
        <OrderDetailsCardHeading>
          {shippingBillingCard.shippingMethod}
        </OrderDetailsCardHeading>
        {get(shipmentMethod, 'businessDays') ? (
          <Box component="p">
            <Span>{get(shipmentMethod, 'name')}:</Span>{' '}
            <Span>
              <ReplaceTokens
                tokenizedString={shippingBillingCard.inBusinessDays}
                substitutions={{
                  numberOfBusinessDays: get(shipmentMethod, 'businessDays'),
                }}
              />
            </Span>
          </Box>
        ) : (
          <Typography>
            {localizedShipmentMethodName || defaultShipmentMethodName}
          </Typography>
        )}
        {showDeliveryDate()}
      </Column>
      <Column span={span} px={px} py={py}>
        {isPickupPoint ? (
          <PickupPointDisplay pickupPointAddressData={shipping} />
        ) : (
          <AddressDetail
            address={shipping}
            title={shippingBillingCard.shippingAddress}
            addressType={AddressType.SHIPPING}
          />
        )}
      </Column>
    </>
  );
};

ShipmentDetails.propTypes = {
  shipping: PropTypes.object,
  shipmentMethod: PropTypes.object,
  arrivalDateRange: PropTypes.shape({
    earliestArrivalDate: PropTypes.string,
    latestArrivalDate: PropTypes.string,
  }),
  columnStyle: PropTypes.object,
};
